/**
 * useDealCountByStage — Fase 1C (perf kanban)
 *
 * Retorna Map<stageId, totalCount> para exibir "30 de N" no header de cada
 * coluna do kanban. Roda 1 query agregada por board (~10ms no SQL).
 *
 * Mutations/realtime atualizam o map atomicamente via setQueryData
 * (incremento/decremento) em vez de invalidar — mais leve.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DEAL_COUNT_BY_STAGE_KEY } from '../queryKeys';
import { getClient } from '@/lib/supabase/client';
import { useAuth } from '@/context/AuthContext';

export type StageCountMap = Map<string, number>;

interface CountRow {
  stage_id: string;
  total: number;
}

export const useDealCountByStage = (boardId: string, searchTerm?: string) => {
  const { user, loading: authLoading } = useAuth();
  const search = searchTerm && searchTerm.trim().length >= 2 ? searchTerm.trim() : undefined;

  return useQuery<StageCountMap>({
    queryKey: [...DEAL_COUNT_BY_STAGE_KEY(boardId), search ?? ''],
    queryFn: async ({ signal }) => {
      const sb = getClient();
      let q = sb.rpc('count_deals_per_stage', {
        p_board_id: boardId,
        ...(search ? { p_search: search } : {}),
      });
      if (signal) q = q.abortSignal(signal);
      const { data, error } = await q;
      if (error) throw error;
      const rows = (data || []) as CountRow[];
      const map: StageCountMap = new Map();
      for (const row of rows) {
        map.set(row.stage_id, Number(row.total) || 0);
      }
      return map;
    },
    staleTime: 2 * 60 * 1000,
    enabled: !authLoading && !!user && !!boardId && !boardId.startsWith('temp-'),
  });
};

/**
 * Helper pra ajustar a contagem de um stage no cache local (sem refetch).
 * Usado por mutations/realtime que sabem que algo mudou pontualmente.
 */
export function adjustStageCount(
  queryClient: ReturnType<typeof useQueryClient>,
  boardId: string,
  stageId: string,
  delta: number
): void {
  if (!boardId || !stageId) return;
  // setQueriesData com prefix-match: ajusta TODAS as variantes da queryKey
  // (com e sem search), senão o contador fica stale quando há filtro ativo.
  queryClient.setQueriesData<StageCountMap>(
    { queryKey: DEAL_COUNT_BY_STAGE_KEY(boardId) },
    (old) => {
      if (!(old instanceof Map)) return old;
      const next = new Map(old);
      const current = next.get(stageId) ?? 0;
      next.set(stageId, Math.max(0, current + delta));
      return next;
    }
  );
}
